import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { staffApi, formatApiError } from "@/lib/api";
import { ShieldCheck } from "lucide-react";
import { ReauthCodeFields } from "@/components/auth/ReauthCodeDialog";

/**
 * Manager Approval popup — triggered when a user with Order/POS Edit
 * (but not Delete) tries to Void or Refund an order.
 *
 * Jun 2026: the manager authorises with a one-time code emailed to their
 * own work address (passwords are gone). The popup posts the verified
 * code to `/api/pos-approvals/inline`, which re-checks it server-side,
 * verifies the delete permission and executes the void/refund.
 *
 * If the requester would rather send it to the queue for later approval,
 * they can hit "Send for later" instead.
 */
export default function ManagerApprovalDialog({
  open,
  onOpenChange,
  orderId,
  kind,                 // "void" | "refund"
  payload,              // body that would normally go to /void or /refund
  actionLabel,          // e.g. "Void order" or "Refund order"
  onApproved,           // called after inline approval succeeds
  onQueued,             // called after "Send for later" succeeds
}) {
  const [email, setEmail] = useState("");
  const [pendingId, setPendingId] = useState("");
  const [code, setCode] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setEmail(""); setPendingId(""); setCode(""); setNote(""); };

  useEffect(() => { if (open) reset(); }, [open]);

  const doInlineApprove = async () => {
    if (!pendingId || code.trim().length !== 6) {
      toast.error("Enter the 6-digit code emailed to the manager");
      return;
    }
    setBusy(true);
    try {
      const r = await staffApi.post("/pos-approvals/inline", {
        approver_pending_id: pendingId,
        approver_code: code.trim(),
        order_id: orderId,
        kind,
        payload,
      });
      toast.success(`${actionLabel} approved`);
      onApproved?.(r.data);
      onOpenChange(false);
      reset();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const doSendToQueue = async () => {
    setBusy(true);
    try {
      const r = await staffApi.post("/pos-approvals", { order_id: orderId, kind, payload, note });
      toast.success(`${actionLabel} sent for manager approval`);
      onQueued?.(r.data);
      onOpenChange(false);
      reset();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onOpenChange(false); reset(); } }}>
      <DialogContent className="max-w-md" data-testid="manager-approval-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-primary" />
            Manager approval required
          </DialogTitle>
          <DialogDescription>
            {actionLabel} needs a user with <b>Order/POS · Delete</b> to authorise. A manager can enter their email to receive a one-time code now, or you can send this to the approval queue for later.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <ReauthCodeFields
            purpose="manager_approval"
            askEmail
            email={email}
            setEmail={setEmail}
            pendingId={pendingId}
            setPendingId={setPendingId}
            code={code}
            setCode={setCode}
            busy={busy}
            setBusy={setBusy}
            idPrefix="approval"
          />
          <div>
            <Label>Note (optional — visible in the approvals queue)</Label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="approval-note"
              placeholder="e.g. Wrong price captured"
            />
          </div>
        </div>

        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy} data-testid="approval-cancel">Cancel</Button>
          <Button variant="outline" onClick={doSendToQueue} disabled={busy} data-testid="approval-send-queue">
            Send for later
          </Button>
          <Button onClick={doInlineApprove} disabled={busy || !pendingId || code.trim().length !== 6} data-testid="approval-approve-now">
            {busy ? "Working…" : `Approve & ${actionLabel}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
